// ── gameStatesStore — batched Unabated gameState map ──────────────
//
// Polls hydra's /api/analytics/game-states for the union of visible
// dashboard gameKeys and stores the latest result. Consumers (the
// value dashboard's GameTable, marketStore.getGames for "final"
// override, etc.) read from this store instead of opening per-game
// SSE connections.
//
// Pattern: ValueDashboard owns the visible-key set + the polling
// effect; everyone else just reads via `useGameStateFor(gameKey)` or
// `useGameStatesStore.getState().getState(gameKey)` for non-React
// consumers.

import { create } from 'zustand';
import type { UnabatedGameState } from '../services/hydraAnalytics';

/** `sourceEpoch\0gameKey` → highest rev applied. Module scope, not store
 *  state: it is bookkeeping for the guard, and putting it in the store would
 *  make every frame a subscriber-visible change. */
const _wireRevs = new Map<string, number>();
/** Has the `gamestate` WS lane written this game? (`_wireRevs` is keyed
 *  `<sourceEpoch>\u0000<gameKey>`.) */
function wireOwns(gameKey: string): boolean {
  const suffix = `\u0000${gameKey}`;
  for (const k of _wireRevs.keys()) if (k.endsWith(suffix)) return true;
  return false;
}

/** Test seam — the rev guard is the whole correctness property here. */
export function __resetGameStateRevsForTests(): void {
  _wireRevs.clear();
}

interface GameStatesStore {
  byGameKey: Map<string, UnabatedGameState | null>;
  /** Browser receipt time of the last complete fetch. Drives transport staleness. */
  lastFetchMs: number;
  /** Producer publication time. Never substituted for a source observation. */
  lastPublicationMs: number | null;
  /** Local time of the latest poll attempt, successful or failed. A failed
   *  poll updates this even when its error text is unchanged, so consumers
   *  can stop trusting an aged snapshot without running their own timers. */
  lastAttemptMs: number;
  /** Last fetch error (if any). Null when the most recent fetch succeeded. */
  error: string | null;
  /** Requested records rejected individually; their canonical state is unknown.
   * Explicit producer nulls are not validation failures. */
  invalidGameKeys: Set<string>;

  setStates: (states: Record<string, UnabatedGameState | null>, serverTs: number | null, receivedAtMs?: number, invalidKeys?: readonly string[]) => void;
  /**
   * Apply one `gameState` WS frame.
   *
   * REV IS THE ONLY ORDERING AUTHORITY. Frames for one key can arrive from
   * more than one socket (the firehose plus any core feed), and a slow socket's
   * older frame must not overwrite a newer one — so an equal-or-lower rev
   * inside the same `sourceEpoch` is dropped. A DIFFERENT epoch means the
   * producer restarted and its rev line began again, so it always wins: the
   * numbers are not comparable across epochs and treating them as such would
   * freeze the key until the new line happened to pass the old high-water mark.
   *
   * `state: null` is a RETIREMENT tombstone — the lane no longer asserts this
   * game. It is stored as null rather than deleted, because null already means
   * "hydra has no state for this key" to every reader, whereas a missing entry
   * would silently fall back to whatever a stale poll left behind.
   */
  applyWireState: (
    gameKey: string,
    state: UnabatedGameState | null,
    meta: { rev: number; sourceEpoch: string; serverTs: number },
  ) => void;
  setError: (message: string | null, attemptedAtMs?: number) => void;
  /** Returns the latest known state for a gameKey, or null if hydra
   *  hasn't seen it yet (or the poller hasn't fetched it). */
  getState: (gameKey: string) => UnabatedGameState | null;
  /** Live-frame counter — the probe that says the WS lane is actually feeding
   *  this store, so a silent regression to poll-only is visible. */
  wireFrames: number;
}

export const useGameStatesStore = create<GameStatesStore>((set, get) => ({
  byGameKey: new Map(),
  lastFetchMs: 0,
  lastPublicationMs: null,
  lastAttemptMs: 0,
  error: null,
  wireFrames: 0,
  invalidGameKeys: new Set(),
  setStates: (states, serverTs, receivedAtMs = Date.now(), invalidKeys = []) => {
    // MERGE, not replace. This used to replace the map wholesale, which was
    // right when the poll was the only writer: hydra returned the full
    // requested set each cycle, so absence meant "no longer subscribed".
    //
    // The WS lane changed that. It writes keys the poll's request set may not
    // contain, and it writes them far more often, so a wholesale replace would
    // delete live state on every poll cycle and make the dashboard flicker at
    // exactly 1/pollInterval. Merging keeps both writers additive, and
    // `applyWireState`'s rev guard is what stops them fighting over a key.
    //
    // The poll's remaining job is `consensus`, which rides no WS channel — so
    // where a wire state already exists, only that block is taken from the
    // poll and the live fields are left alone.
    const cur = get().byGameKey;
    // An EMPTY poll scope means the page stopped asking (main's contract: the
    // map clears) — but only the POLL's keys are its to clear; a game the WS
    // lane asserts stays until the lane retires it.
    const emptyScope = Object.keys(states).length === 0;
    const next = emptyScope
      ? new Map([...cur].filter(([k]) => wireOwns(k)))
      : new Map(cur);
    for (const [k, v] of Object.entries(states)) {
      const live = cur.get(k);
      // Only a key the WIRE has written keeps its live fields: a value that came
      // from an earlier POLL must be replaced by the newer poll, or a score
      // update on a box with no gamestate lane would never land.
      if (v && live && wireOwns(k)) next.set(k, { ...live, consensus: v.consensus });
      else next.set(k, v);
    }
    set({ byGameKey: next, lastFetchMs: receivedAtMs, lastPublicationMs: serverTs,
      lastAttemptMs: receivedAtMs, error: null, invalidGameKeys: new Set(invalidKeys) });
  },
  applyWireState: (gameKey, state, meta) => {
    const revKey = `${meta.sourceEpoch}\u0000${gameKey}`;
    const seen = _wireRevs.get(revKey);
    if (seen !== undefined && meta.rev <= seen) return;   // stale or duplicate
    // A new epoch invalidates every rev we hold for the old one on this key.
    for (const k of _wireRevs.keys()) {
      if (k.endsWith(`\u0000${gameKey}`) && k !== revKey) _wireRevs.delete(k);
    }
    _wireRevs.set(revKey, meta.rev);
    const cur = get().byGameKey;
    const next = new Map(cur);
    // Carry the poll-only `consensus` block across a wire update, so switching
    // the live fields to the WS does not blank the panel's U row between polls.
    const prev = cur.get(gameKey);
    next.set(gameKey, state && prev ? { ...state, consensus: state.consensus ?? prev.consensus } : state);
    set({
      byGameKey: next,
      lastFetchMs: meta.serverTs || Date.now(),
      lastAttemptMs: Date.now(),
      error: null,
      wireFrames: get().wireFrames + 1,
    });
  },
  setError: (message, attemptedAtMs = Date.now()) =>
    set({ error: message, lastAttemptMs: attemptedAtMs }),
  getState: (gameKey) => get().byGameKey.get(gameKey) ?? null,
}));

/**
 * React hook for one game's state. Returns `null` until the poller
 * has fetched it (or until hydra reports the gameKey unknown).
 */
export function useGameStateFor(gameKey: string | null | undefined): UnabatedGameState | null {
  return useGameStatesStore((s) => (gameKey ? s.byGameKey.get(gameKey) ?? null : null));
}
